import _unescape from 'lodash/unescape';
import { styled } from '@material-ui/core';

import type {
  FieldItemList,
  TableCells,
  AloneEventInformation,
  BaseMassMessage,
} from 'models';
import safeParseHtml from 'shared/utils/safeParseHtml';

interface BoardEventInformationFields<T extends object = {}> {
  baseInfo: FieldItemList<T>[];
  planedTime: FieldItemList<T>[];
  actualTime: FieldItemList<T>[];
  description: FieldItemList<T>[];
}

interface EventInformationFields<T extends object = {}> {
  baseInfo: FieldItemList<T>[];
  planedTime: FieldItemList<T>[];
  actualTime: FieldItemList<T>[];
}

const Description = styled('span')(({ theme }) => ({
  fontSize: 14,
  color: theme.custom.darkBlue,
}));

export const eventInformationFields: EventInformationFields<AloneEventInformation> =
  {
    baseInfo: [
      {
        id: 'Name',
        key: '##attribute#uds_eventinformation#uds_eventname',
        format: 'string',
        render: (eventInformation) =>
          eventInformation.Name &&
          safeParseHtml(_unescape(eventInformation.Name)),
      },
      {
        id: 'Type.Name',
        key: '##attribute#uds_eventinformation#uds_productevent',
        format: 'string',
      },
      {
        id: 'Status.Label',
        key: '##attribute#uds_eventinformation#uds_eventstatus',
        format: 'string',
      },
      {
        id: 'Location',
        key: '##attribute#uds_eventinformation#uds_location',
        format: 'string',
      },
      {
        id: 'Description',
        key: '##attribute#uds_eventinformation#uds_eventdescription',
        format: 'html',
        header: {
          position: 'before',
          text: 'Tapahtuman kuvaus',
        },
        renderFullItem: (eventInformation) => (
          <li>
            <Description>
              {eventInformation.Description &&
                safeParseHtml(_unescape(eventInformation.Description))}
            </Description>
          </li>
        ),
      },
    ],
    planedTime: [
      {
        id: 'PlannedStart',
        key: '##attribute#uds_eventinformation#uds_plannedstartingtime',
        format: 'datetime',
      },
      {
        id: 'PlannedEnd',
        key: '##attribute#uds_eventinformation#uds_plannedendingtime',
        format: 'datetime',
      },
      {
        id: 'PlannedDuration',
        key: '##attribute#uds_eventinformation#uds_plannedduration',
        format: 'string',
      },
    ],
    actualTime: [
      {
        id: 'ActualStart',
        key: '##attribute#uds_eventinformation#uds_actualstartingtime',
        format: 'datetime',
      },
      {
        id: 'ActualEnd',
        key: '##attribute#uds_eventinformation#uds_actualendingtime',
        format: 'datetime',
      },
      {
        id: 'ActualDuration',
        key: '##attribute#uds_eventinformation#uds_actualduration',
        format: 'string',
      },
    ],
  };

export const auditingEventInformationFields: BoardEventInformationFields<AloneEventInformation> =
  {
    baseInfo: [
      {
        id: 'Name',
        key: '##attribute#uds_eventinformation#uds_eventname',
        format: 'string',
        render: (eventInformation) =>
          eventInformation.Name && _unescape(eventInformation.Name),
      },
      {
        id: 'Type.Name',
        key: '##attribute#uds_eventinformation#uds_productevent',
        format: 'string',
      },
      {
        id: 'Status.Label',
        key: '##attribute#uds_eventinformation#uds_eventstatus',
        format: 'string',
      },
    ],
    planedTime: [
      {
        id: 'AuditDeliveryDate',
        key: 'Toimitusajankohta',
        format: 'date',
      },
      {
        id: 'AuditReturnNeededDate',
        key: 'Palautus toivottu viimeistään',
        format: 'date',
      },
      {
        id: 'AuditAuditingDone',
        key: 'Tilintarkastus suoritettu',
        format: 'date',
      },
    ],
    actualTime: [
      {
        id: 'ActualStart',
        key: '##attribute#uds_eventinformation#uds_actualstartingtime',
        format: 'date',
      },
      {
        id: 'ActualEnd',
        key: '##attribute#uds_eventinformation#uds_actualendingtime',
        format: 'date',
      },
      {
        id: 'ActualDuration',
        key: '##attribute#uds_eventinformation#uds_actualduration',
        format: 'string',
      },
    ],
    description: [
      {
        id: 'Description',
        key: '##attribute#uds_eventinformation#uds_eventdescription',
        format: 'html',
        showTitle: false,
      },
    ],
  };

export const eventInformationTableCells: TableCells<BaseMassMessage>[] = [
  {
    id: 'Subject',
    type: 'string',
    label: 'Aihe',
    small: true,
    xs: true,
    render: (eventInformation) =>
      eventInformation.Subject && _unescape(eventInformation.Subject),
  },
  {
    id: 'Description',
    type: 'html',
    label: 'Kuvaus',
    small: true,
    xs: true,
    render: (eventInformation) =>
      eventInformation.Description &&
      safeParseHtml(_unescape(eventInformation.Description)),
  },
  {
    id: 'DistributedOn',
    type: 'datetime',
    label: 'Lähetetty',
    small: true,
    xs: false,
    style: { width: '18%' },
  },
];
